import React, { useMemo, useState } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  ChatWithDataMinMaxProposal,
  ChatWithDataMinMaxProposalRow,
} from '@recurrency/core-api-schema/dist/chatWithData/chatWithData';
import { IntegratedErps, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { InputNumber, Steps, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';

import { DailyWorkflowProjectedInventoryCard } from 'pages/dailyWorkflow/DailyWorkflowProjectedInventoryCard';
import { getMinMaxExportRestrictions, MIN_MAX_EXPORT_LIMIT } from 'pages/purchasing/PlanningPage/utils';

import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { NotificationLink } from 'components/Links';
import { Modal } from 'components/Modal';
import { notifyAndDownload } from 'components/recipes/DownloadButton';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { CloseableModalProps } from 'utils/asyncModal';
import { formatNumber, formatUSD, getErpName, joinIdNameObj } from 'utils/formatting';
import { routes } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { getTenantSetting } from 'utils/tenantSettings';
import { MinMaxSubmissionType, PlanningModalType, track, TrackEvent } from 'utils/track';

import { getProjectedInventoryChange, getTotalInventoryChange, isValidMinMax } from './utils';

enum WizardStep {
  Review = 0,
  Impact = 1,
  Export = 2,
}

type EditableRow = Omit<ChatWithDataMinMaxProposalRow, 'newMin' | 'newMax'> & { newMin?: number; newMax?: number };

const rowKey = (row: EditableRow) => `${row.itemUid}|${row.locationId}`;

const toCell = (value: string | number | undefined) =>
  typeof value === 'number' ? { value, type: 'number' as const } : { value: value ?? '', type: 'string' as const };

const formatLocation = (row: EditableRow) => joinIdNameObj({ foreignId: row.locationId, name: row.locationName });

const formatMinMax = (min?: number, max?: number) =>
  min === undefined && max === undefined ? '-' : `${formatNumber(min ?? 0)} / ${formatNumber(max ?? 0)}`;

export function MinMaxProposalWizardModal({
  proposal,
  onClose,
}: { proposal: ChatWithDataMinMaxProposal } & CloseableModalProps<boolean>) {
  const { activeTenant, activeUser } = useGlobalApp();
  const [step, setStep] = useState(WizardStep.Review);
  const [rows, setRows] = useState<EditableRow[]>(proposal.rows);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>(() => proposal.rows.map(rowKey));

  const selectedEditableRows = useMemo(() => {
    const keys = new Set(selectedKeys);
    return rows.filter((row) => keys.has(rowKey(row)));
  }, [rows, selectedKeys]);
  const hasInvalidSelectedRows = selectedEditableRows.some((row) => !isValidMinMax(row.newMin, row.newMax));
  // Past the Review step every selected row has passed isValidMinMax.
  const selectedRows = selectedEditableRows as ChatWithDataMinMaxProposalRow[];

  const { isTxnApiTenantWithSynchronousExports, isRestrictedToSmallExportsOnly } = getMinMaxExportRestrictions(
    activeTenant,
    activeUser,
  );
  const isOverExportLimit =
    (isTxnApiTenantWithSynchronousExports || isRestrictedToSmallExportsOnly) &&
    selectedRows.length > MIN_MAX_EXPORT_LIMIT;

  const updateRow = (key: string, field: 'newMin' | 'newMax', value: number | string | null) => {
    setRows((prev) =>
      prev.map((row) => {
        if (rowKey(row) !== key) return row;
        const updated = { ...row, [field]: typeof value === 'number' ? value : undefined };
        return {
          ...updated,
          projectedInventoryChange: getProjectedInventoryChange(row, updated.newMin ?? 0, updated.newMax ?? 0),
        };
      }),
    );
  };

  const renderMinMaxInput = (row: EditableRow, field: 'newMin' | 'newMax') => (
    <InputNumber
      size="small"
      min={0}
      precision={0}
      value={row[field]}
      style={isValidMinMax(row.newMin, row.newMax) ? undefined : { borderColor: colors.danger[500] }}
      onChange={(value) => updateRow(rowKey(row), field, value)}
    />
  );

  const columns: ColumnType<EditableRow>[] = [
    {
      title: 'Item',
      render: (_, row) => (
        <>
          <div>{row.itemCode}</div>
          <Typography.Text type="secondary">{row.itemName}</Typography.Text>
        </>
      ),
    },
    { title: 'Location', render: (_, row) => formatLocation(row) },
    { title: 'Current Min / Max', render: (_, row) => formatMinMax(row.currentMin, row.currentMax) },
    { title: 'Recommended Min / Max', render: (_, row) => formatMinMax(row.recommendedMin, row.recommendedMax) },
    { title: 'New Min', render: (_, row) => renderMinMaxInput(row, 'newMin') },
    { title: 'New Max', render: (_, row) => renderMinMaxInput(row, 'newMax') },
    {
      title: 'Inventory Change',
      align: 'right',
      render: (_, row) => formatUSD(row.projectedInventoryChange),
    },
    { title: 'Agent Reason', dataIndex: 'reason', ellipsis: true },
  ];

  const handleDownload = () =>
    notifyAndDownload(
      () =>
        Promise.resolve({
          data: [
            ['Item', 'Item Name', 'Location', 'Current Min', 'Current Max', 'New Min', 'New Max', 'Agent Reason'].map(
              toCell,
            ),
            ...selectedRows.map((row) =>
              [
                row.itemCode,
                row.itemName,
                formatLocation(row),
                row.currentMin,
                row.currentMax,
                row.newMin,
                row.newMax,
                row.reason,
              ].map(toCell),
            ),
          ],
        }),
      proposal.title,
    );

  const handleExport = async () => {
    const entityName = 'min/max changes';
    const hasAsyncMinMaxExportsSetting = getTenantSetting(TenantSettingKey.FeatureAsyncMinMaxExports);
    const submitNotification = createSubmissionNotification({
      entityName: 'Min/Max Changes',
      expectedWaitSeconds: 60 + Math.round(selectedRows.length / 5_000) * 30,
      erpType: activeTenant.erpType,
      submittingMessage:
        activeTenant.erpType === IntegratedErps.P21 && !hasAsyncMinMaxExportsSetting
          ? `Updating ${entityName} to ${activeTenant.erpType.toUpperCase()}`
          : `Saving ${entityName}`,
    });

    try {
      onClose(true);
      const response = await coreApiFetch(schemas.purchasing.postUpdateItemLocationMinMax, {
        bodyParams: {
          updates: selectedRows.map((row) => ({
            itemId: row.itemCode,
            itemUid: row.itemUid,
            locationId: row.locationId,
            newMin: row.newMin,
            newMax: row.newMax,
          })),
        },
      });
      const { asyncExportJobId } = response.data;

      track(TrackEvent.DemandPlanning_MinMax_Submission, {
        modalType: PlanningModalType.ChatMinMax,
        itemCount: selectedRows.length,
        submissionType: MinMaxSubmissionType.Custom,
      });
      submitNotification.success({
        description:
          asyncExportJobId && hasAsyncMinMaxExportsSetting
            ? (notificationKey) => (
                <>
                  <div>Min/max changes will export to {getErpName(activeTenant.erpType)} in background.</div>
                  <NotificationLink notificationKey={notificationKey} to={routes.monitoring.exportJobs()}>
                    View Export Jobs
                  </NotificationLink>
                </>
              )
            : undefined,
      });
    } catch (err) {
      submitNotification.error(err);
    }
  };

  const exportLabel =
    activeTenant.erpType === IntegratedErps.P21 ? `Send to ${getErpName(activeTenant.erpType)}` : 'Save';

  return (
    <Modal
      visible
      width={1100}
      title={proposal.title}
      onCancel={() => onClose(false)}
      destroyOnClose
      footer={
        <>
          {step === WizardStep.Review ? (
            <Button onClick={() => onClose(false)}>Cancel</Button>
          ) : (
            <Button onClick={() => setStep(step - 1)}>Back</Button>
          )}
          {step !== WizardStep.Export ? (
            <Button
              type="primary"
              disabled={selectedRows.length === 0 || hasInvalidSelectedRows}
              onClick={() => setStep(step + 1)}
            >
              Next
            </Button>
          ) : (
            <Tooltip
              title={
                isOverExportLimit
                  ? isRestrictedToSmallExportsOnly
                    ? `Exports of >${MIN_MAX_EXPORT_LIMIT} records are available once onboarding is complete`
                    : `Export is limited to ${MIN_MAX_EXPORT_LIMIT} records at a time`
                  : undefined
              }
            >
              <Button
                type="primary"
                permissionsRequired={[TenantUserPermission.ActionExportMinMax]}
                disabled={isOverExportLimit}
                onClick={handleExport}
              >
                {exportLabel}
              </Button>
            </Tooltip>
          )}
        </>
      }
    >
      <Steps
        current={step}
        className={css`
          margin-bottom: 24px;
        `}
      >
        <Steps.Step title="Review changes" />
        <Steps.Step title="Inventory impact" />
        <Steps.Step title="Export" />
      </Steps>

      {step === WizardStep.Review && (
        <>
          <div
            className={css`
              margin-bottom: 12px;
              color: ${colors.neutral[600]};
            `}
          >
            {proposal.rationale} Edit values or deselect rows you don't want to export.
          </div>
          <Table
            size="small"
            rowKey={rowKey}
            columns={columns}
            dataSource={rows}
            rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
            pagination={rows.length > 10 ? { defaultPageSize: 10 } : false}
            scroll={{ x: true }}
          />
        </>
      )}

      {step === WizardStep.Impact && (
        <DailyWorkflowProjectedInventoryCard
          minMaxUpdates={{
            items: selectedRows.map((row) => ({
              itemId: row.itemCode,
              itemCode: row.itemCode,
              itemName: row.itemName,
              locationId: row.locationId,
              locationName: row.locationName,
              currentMin: row.currentMin ?? 0,
              currentMax: row.currentMax ?? 0,
              recommendedMin: row.recommendedMin ?? 0,
              recommendedMax: row.recommendedMax ?? 0,
              newMin: row.newMin,
              newMax: row.newMax,
              projectedInventoryChange: row.projectedInventoryChange,
            })),
            totalCount: selectedRows.length,
          }}
        />
      )}

      {step === WizardStep.Export && (
        <FlexSpace direction="column" alignItems="flex-start" gap={12}>
          <div>
            {formatNumber(selectedRows.length)} min/max changes, projected inventory change of{' '}
            {formatUSD(getTotalInventoryChange(selectedRows))}, will be{' '}
            {activeTenant.erpType === IntegratedErps.P21 ? `sent to ${getErpName(activeTenant.erpType)}` : 'saved'}.
          </div>
          <Button onClick={handleDownload}>Download as Excel</Button>
        </FlexSpace>
      )}
    </Modal>
  );
}
